import React, { useRef, useState } from 'react';
import { Stage, Layer, Rect, Circle, Line, Ellipse } from 'react-konva';
import { SketchPicker } from 'react-color';
import { Button } from '@ohif/ui';

const DrawingCanvas = ({ onSave }) => {
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#df4b26');
  const [fillColor, setFillColor] = useState('transparent');
  const [shapes, setShapes] = useState([]);
  const isDrawing = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const [showStrokeColor,setShowStrokeColor] = useState(false)
  const [showFillColor,setShowFillColor] = useState(false)

  const handleMouseDown = (e) => {
    const pos = e.target.getStage().getPointerPosition();
    startPos.current = pos;

    if (tool === 'eraser') {
      handleEraser(pos);
    } else {
      isDrawing.current = true;
      const newShape = {
        type: tool,
        x: pos.x,
        y: pos.y,
        points: [pos.x, pos.y],
        width: 0,
        height: 0,
        stroke: color,
        fill: fillColor,
      };

      setShapes([...shapes, newShape]);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing.current) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const lastShapeIndex = shapes.length - 1;
    const lastShape = shapes[lastShapeIndex];

    if (tool === 'pen') {
      lastShape.points = lastShape.points.concat([point.x, point.y]);
    } else {
      lastShape.width = point.x - startPos.current.x;
      lastShape.height = point.y - startPos.current.y;
    }

    shapes.splice(lastShapeIndex, 1, lastShape);
    setShapes([...shapes]);
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const handleToolChange = (newTool) => {
    setTool(newTool);
  };

  const handleColorChange = (newColor) => {
    setColor(newColor.hex);
  };

  const handleFillColorChange = (newColor) => {
    setFillColor(newColor.hex);
  };

  const handleSave = () => {
    const dataURL = stageRef.current.toDataURL();
    onSave(dataURL);
  };

  const handleEraser = (point) => {
    const updatedShapes = shapes.filter((shape) => !isPointInShape(point, shape));
    setShapes(updatedShapes);
  };

  const isPointInShape = (point, shape) => {
    switch (shape.type) {
      case 'pen':
        return shape.points.some((_, index) => {
          if (index % 2 === 0) {
            const x = shape.points[index];
            const y = shape.points[index + 1];
            return Math.abs(point.x - x) < 5 && Math.abs(point.y - y) < 5;
          }
          return false;
        });
      case 'rect':
        return (
          point.x >= shape.x &&
          point.x <= shape.x + shape.width &&
          point.y >= shape.y &&
          point.y <= shape.y + shape.height
        );
      case 'circle':
        const radius = Math.sqrt(shape.width ** 2 + shape.height ** 2);
        return (
          Math.sqrt((point.x - shape.x) ** 2 + (point.y - shape.y) ** 2) <=
          radius
        );
      case 'ellipse':
        const rx = Math.abs(shape.width / 2);
        const ry = Math.abs(shape.height / 2);
        const h = shape.x + rx;
        const k = shape.y + ry;
        return (
          (Math.pow(point.x - h, 2) / Math.pow(rx, 2)) +
          (Math.pow(point.y - k, 2) / Math.pow(ry, 2)) <=
          1
        );
      default:
        return false;
    }
  };

  const renderShape = (shape, index) => {
    switch (shape.type) {
      case 'pen':
        return (
          <Line
            key={index}
            points={shape.points}
            stroke={shape.stroke}
            strokeWidth={2}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
          />
        );
      case 'rect':
        return (
          <Rect
            key={index}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      case 'circle':
        const radius = Math.sqrt(shape.width ** 2 + shape.height ** 2);
        return (
          <Circle
            key={index}
            x={shape.x}
            y={shape.y}
            radius={radius}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      case 'ellipse':
        return (
          <Ellipse
            key={index}
            x={shape.x}
            y={shape.y}
            radiusX={Math.abs(shape.width / 2)}
            radiusY={Math.abs(shape.height / 2)}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      default:
        return null;
    }
  };

  const handleShowFillColor = ()=>{
    setShowFillColor(!showFillColor)
  }

  const handleStrokeColor = ()=>{
    setShowStrokeColor(!showStrokeColor)
  }

  return (
    <div>
      <div className='mb-2'>
        <Button className='btn-primary ml-1' onClick={() => handleToolChange('pen')}>Pen</Button>
        <Button className='btn-primary ml-1' onClick={() => handleToolChange('rect')}>Rectangle</Button>
        <Button className='btn-primary ml-1' onClick={() => handleToolChange('circle')}>Circle</Button>
        <Button className='btn-primary ml-1' onClick={() => handleToolChange('ellipse')}>Ellipse</Button>
        <Button className='btn-primary ml-1' onClick={() => handleToolChange('eraser')}>Eraser</Button>
        <div className='mt-1'>
          <Button className='btn-primary ml-1' onClick={handleStrokeColor}>Stroke Color:</Button>
          { showStrokeColor && <div style={{zIndex:"1000",position:"absolute"}}><SketchPicker color={color} onChangeComplete={handleColorChange} /></div> }
          <Button className='btn-primary ml-1' onClick={handleShowFillColor}>Fill Color:</Button>
          {showFillColor &&<div style={{zIndex:"1000",position:"absolute"}}>
          <SketchPicker
            color={fillColor}
            onChangeComplete={handleFillColorChange}
          />
            </div>}
        </div>

      </div>
      <Stage
        width={window.innerWidth * 0.4}
        height={window.innerHeight * 0.4}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        className=' border-solid border-4 border-black'
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
        ref={stageRef}
      >
        <Layer>{shapes.map((shape, index) => renderShape(shape, index))}</Layer>
      </Stage>
      <Button className="mt-1 bg-primary px-3 py-1 rounded-md" onClick={handleSave}>
        Save
      </Button>
    </div>
  );
};

export default DrawingCanvas;
